import React, { useState, useEffect, useRef } from "react";
import { Alert, Snackbar } from "@mui/material";

import {
    API_ENDPOINTS,
} from "../../../utils/constants";
import { generateChatId, generatedBotMessageId, generatedUserMessageId, generateTimestamp, MESSAGE_DIRECTIONS, MESSAGE_STATUSES } from "../../../utils/ChatManager";
import { getLanguageData } from "../../../utils/AppConstants";
import { useAppContext } from "../../../context/AppContext";
import { useSnackbar } from "../../CommonComponents/Toast/Toast";
import BotChatHead from "../../CommonComponents/ChatHeads/BotChatHead";
import botLogo from "../../../assets/images/bot.png";
import CollapsedHeader from "../../Header/CollapsedHeader";
import ExpendedHeader from "../../Header/ExpendedHeader";
import Loader from "../../CommonComponents/ChatHeads/Loader";
import picLogo from "../../../assets/images/picLogo.png";
import Swal from "sweetalert2";
import useLocalization from '../../../hooks/useLocalization';
import UserChatHead from "../../CommonComponents/ChatHeads/UserChatHead";
import SuggestedQuestions from "../../CommonComponents/suggesedQuestion/SuggestedQuestions";
import { AlertCircle } from "lucide-react";
import { Trans } from "react-i18next";
import { useLogger } from "../../../utils/UnifiedLogger";
import SepratedInputChnages from "../../CommonComponents/InputUI/SepratedInputChnages";
import { useReduxChat } from '../../../hooks/useReduxChat';
import { useDispatch, useSelector } from "react-redux";
import { useDialog } from "../../CommonComponents/Dialog/useDialog";
import { useNavigate } from "react-router-dom";

import {
    setIsTextLanguageDetecting,
    setTextLanguageDetectionData,
    setTextLanguageDetectionError,
    setIsContentGenerating,
    setContentGenerationData,
    setContentGenerationError,
    setIsNerDetecting,
    setNerDetectionData,
    setNerDetectionError,
    setSelectedChatId,
    setCurrentUserChatId,
    setCurrentBotChatId,
    setSelectedSocketLanguageData,
    lockVoiceFlowForChat,
    setCurrentInterimTranscript,
    setTranscriptType,
    setAccumulatedFinalTranscript,
    setDisplayTranscript,
    setRecordedBase64Data,
    lockTextFlowForChat,
    unlockTextFlowForChat,
    setIsSocketRecording
} from "../../../store/features/message/textContentSlice";

import {
    selectIsTextLanguageDetecting,
    selectTextLanguageDetectionData,
    selectTextLanguageDetectionError,
    selectIsContentGenerating,
    selectContentGenerationData,
    selectContentGenerationError,
    selectSelectedChatId,
    selectCurrentUserChatId,
    selectCurrentBotChatId,
    selectAccumulatedFinalTranscript,
    selectCurrentInterimTranscript,
    selectSelectedSocketLanguageData,
    selectRecordedBase64Data,
    selectIsVoiceFlow,
    selectIsSocketDataReadyToSend,
    selectVoiceFlowLocks,
    selectTextFlowLocks,
    selectIsTextFlow
} from "../../../store/features/message/textContentSelectors";


import {
    selectInputValue,
    selectIsNewChatClicked,
    selectIsGrievanceRegistering
} from "../../../store/features/ui/uiSelectors";

import {
    setInputValue,
    clearInputValue,
    setInputDisabled,
    setSendButtonDisabled,
    setMicButtonDisabled,
    setIsNewChatClicked,
    setIsGrievanceRegistering
} from "../../../store/features/ui/uiSlice";
import { updateFlowStates } from "../../../store/features/message/updateFlowStates";
import { trackApiError } from "../../../utils/ErrorTracker";

function ChatPage() {
    const apiKey = import.meta.env.VITE_BHASHINI_API_KEY;
    const NER_ENDPOINT_URL = import.meta.env.VITE_BHASHINI_NER_ENDPOINT_URL;
    const NER_SERVICE_ID = import.meta.env.VITE_BHASHINI_NER_SERVICE_ID;
    const { logInfo, logError } = useLogger("ChatPage", false);
    const dispatch = useDispatch();
    const dialog = useDialog();
    const [copyBar, setCopyBar] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isChatUpdated, setIsChatUpdated] = useState(false);
    const [isComplaintRegistering, setIsComplaintRegistering] = useState(false);
    const [showTranslitrateSuggestions, setShowTranslitrateSuggestions] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { t, setLanguage, getLanguage } = useLocalization();
    const isAsciiLettersOrNumbers = /^[a-zA-Z0-9]+$/;
    const [getFormAnswers, setFormAnswers] = useState(null);
    const [getFilledForms, setFilledForms] = useState(null);
    const [onDynamicFormSubmit, setDynamicFormSubmit] = useState(false);
    const navigate = useNavigate();

    // input states from Redux
    const inputValue = useSelector(selectInputValue);

    // Langauge Detection State from Redux
    const isLanguageDetecting = useSelector(selectIsTextLanguageDetecting);
    const selectedTextLanguageData = useSelector(selectTextLanguageDetectionData);
    const langDetectError = useSelector(selectTextLanguageDetectionError);

    // Content Generation State from Redux
    const isContentGenerating = useSelector(selectIsContentGenerating);
    const contentGenData = useSelector(selectContentGenerationData);
    const contentGenError = useSelector(selectContentGenerationError);

    const selectedChatId = useSelector(selectSelectedChatId);
    const currentUserChatId = useSelector(selectCurrentUserChatId);
    const currentBotChatId = useSelector(selectCurrentBotChatId);

    const isNewChatClicked = useSelector(selectIsNewChatClicked);
    const isGrievanceRegistering = useSelector(selectIsGrievanceRegistering);

    // ALL REFS
    const messagesEndRef = useRef(null);
    const hasSentRef = useRef(false);
    const botRequestRef = useRef(false);
    const isTypingRef = useRef(false);
    const prevMessageCountRef = useRef(0);
    const currentRequestRef = useRef({
        // isVoiceFlow: false,
        userMessageId: null,
        botMessageId: null,
        chatId: null,
        inputText: ""
    });
    const isProcessingResponseRef = useRef(false);

    const {
        addMessage,
        updateMessage,
        deleteMessage,
        createMessage,
        chatList,
    } = useReduxChat();

    const {
        userData,
        isNewChat,
        setIsChatStarted,
        setIsNewChat,
        setSelectedLanguage,
        setSelectedLanguageData,
        sharedApiService,
        selectedLanguageData,
        setInitialLanguage,
        isAuthenticated
    } = useAppContext();

    const accumulatedFinalTranscript = useSelector(selectAccumulatedFinalTranscript);
    const currentInterimTranscript = useSelector(selectCurrentInterimTranscript);
    const selectedSocketLanguageData = useSelector(selectSelectedSocketLanguageData);
    const onRecordedAudio = useSelector(selectRecordedBase64Data);
    const isVoiceFlow = useSelector(selectIsVoiceFlow);
    const isTextFlow = useSelector(selectIsTextFlow);
    const isAudioJourneyReadyToSend = useSelector(selectIsSocketDataReadyToSend);
    const isVoiceFlowLocked = useSelector((state) => selectedChatId ? selectVoiceFlowLocks(selectedChatId)(state) : false);
    const isTextFlowLocked = useSelector((state) => selectedChatId ? selectTextFlowLocks(selectedChatId)(state) : false);


    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated]);

    useEffect(() => {
        logInfo('FLOW_STATES', 'Current flow states', {
            isVoiceFlow,
            isVoiceFlowLocked,
            isTextFlow,
            isTextFlowLocked
        });
    }, [isTextFlow, isTextFlowLocked, isVoiceFlow, isVoiceFlowLocked]);

    const botResponseRequest = sharedApiService.sendRequest;
    const nerResponseRequest = sharedApiService.sendRequest;
    const languageDetectionRequest = sharedApiService.sendRequest;
    const registerGrievanceRequest = sharedApiService.sendRequest;

    const enableButtonAndInputAfterResponse = (path) => {
        dispatch(setInputDisabled(false));
        dispatch(setSendButtonDisabled(false));
        dispatch(setMicButtonDisabled(false));
        if (path === "onComplete" || path === "onSuccess") {
            // dispatch(setIsContentGenerating(false));
            dispatch(setIsSocketRecording(false));
            dispatch(setIsContentGenerating(false));
            dispatch(setIsTextLanguageDetecting(false));
        }
        console.log('Button and Input enabled after response', { path });
    };

    /**
     * Clears the input value and resets the state of the bot request and hasSent refs.
     */
    const clearData = () => {
        hasSentRef.current = false;
        botRequestRef.current = false;
        setFilledForms(null);
        setFormAnswers(null);
        dispatch(clearInputValue());
        dispatch(setContentGenerationData(null));
        dispatch(setTextLanguageDetectionData(null));
        dispatch(setAccumulatedFinalTranscript(""));
        dispatch(setCurrentInterimTranscript(""));
        dispatch(setDisplayTranscript(""));
        dispatch(setInputValue(""));
    };

    const handleClose = () => setCopyBar(false);

    /**
     * Handles NER detection by sending a request to the NER endpoint
     * The language to be used for NER detection is determined by the following rules:
     * - If voice flow is enabled, the language of the currently selected socket language is used
     * - If voice flow is disabled, the language detected during the language detection process is used
     * - If the language detected is not supported by the NER endpoint, the language is set to "en"
     * 
     */
    const handleNerDetection = () => {

        const supportedLangs = ["as", "bn", "gu", "hi", "kn", "ml", "te", "mr", "or", "pa", "ta"];

        // ✅ Pick language based on condition
        const selectedLang = (isVoiceFlow || isVoiceFlowLocked)
            ? (selectedSocketLanguageData?.code?.trim() || "en")
            : (selectedTextLanguageData?.lang || "en");

        if (supportedLangs.includes(selectedLang)) {
            logInfo('NER_REQUEST_INITIATED', 'Starting NER request as language is supported', { lang: selectedTextLanguageData?.lang });
            nerResponseRequest({
                method: "POST",
                url: NER_ENDPOINT_URL,
                headers: {
                    "Authorization": apiKey,
                    "Content-Type": "application/json"
                },
                abortable: true,
                isEncrypted: false,
                body: {
                    "config": {
                        "language": {
                            "sourceLanguage": selectedLang
                        },
                        "serviceId": NER_SERVICE_ID
                    },
                    "input": [
                        {
                            "source": currentRequestRef.current.inputText
                        }
                    ]
                },
                onStart: () => {
                    logInfo('NER_REQUEST_STARTED', 'NER request started');
                    dispatch(setIsNerDetecting(true));
                },
                onCancel: (cancelInfo) => {
                    logInfo('BOT_REQUEST_CANCELLED', 'Bot request was cancelled', cancelInfo);
                    {
                        logInfo('LANG_DETECT_COMPLETE', 'Language detection request completed');
                    }
                    dispatch(setIsNerDetecting(false));
                },
                onSuccess: (res) => {
                    logInfo('NER_REQUEST_SUCCESS', 'NER request successful', res);
                    dispatch(setNerDetectionData(res.output[0].nerPrediction));
                    let generatedContentData = {
                        nerContent: res.output[0].nerPrediction,
                        status: MESSAGE_DIRECTIONS.INCOMING,
                        isNerResponding: false,
                    }
                    updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, generatedContentData);
                },
                onError: (err) => {
                    logError('NER_REQUEST_ERROR', 'NER request failed', err);
                    dispatch(setNerDetectionError(err));
                },
                onComplete: () => {
                    dispatch(setIsNerDetecting(false));
                },
            });
        }
    }

    /**
     * Handle content generation request.
     *
     * @param {string} chatId - The selected chat ID.
     *
     * This function will call the language detection API and the content generation API
     * in sequence. It will also handle the voice flow locking and clearing of the input value.
     *
     * The function will also dispatch the action to update the message state when the content generation
     * request is successful.
     *
     * The function will log the selected chat ID and the bot request ID at the start and end of the request.
     *
     * The function will also log any errors that occurred during the request.
     */
    const handleContentGeneration = (chatId) => {
        logInfo(`SELECTED_CHAT_${selectedChatId ? selectedChatId : currentRequestRef.current.chatId}`, `Selected chat ID: ${selectedChatId ? selectedChatId : currentRequestRef.current.chatId}`, {
            chatId,
            currentBotChatId,
            currentUserChatId,
        });

        if (botRequestRef.current) {
            logInfo('DUPLICATE_CALL_PREVENTED', 'Preventing duplicate content generation call');
            return;
        }

        handleNerDetection();

        if ((isVoiceFlow || isVoiceFlowLocked)) {
            dispatch(lockVoiceFlowForChat(chatId));
        }

        if ((isTextFlow || isTextFlowLocked)) {
            dispatch(lockTextFlowForChat(chatId));
        }

        botRequestRef.current = true;
        let grievanceLanguage = (isVoiceFlow || isVoiceFlowLocked)
            ? (selectedSocketLanguageData?.code?.trim() || "en")
            : (selectedTextLanguageData?.lang || "en");

        let botPayload = {
            input: {
                language: grievanceLanguage,
                text: currentRequestRef.current.inputText,
                audio: "",
                context: "JB_system_prompt",
                isVoiceFlow: (isVoiceFlow || isVoiceFlowLocked) ? true : false,
                is_form_data: isFormValid(getFilledForms) ? true : false,
                user_filled_forms: isFormValid(getFilledForms) ? getFilledForms : {},
                userId: userData.id
            },
            output: { format: "text" },
        }


        botResponseRequest({
            method: "POST",
            url: API_ENDPOINTS.generateContent,
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": userData.id,
                "x-source": chatId.replace(/-/g, '_'),
                "x-consumer-id": 1,
            },
            body: botPayload,
            abortable: true,
            isEncrypted: true,
            enableBotStatusPolling: true,
            botStatusConfig: {
                statusUrl: `${API_ENDPOINTS.progressUpdateOnGrievance}?lang=${grievanceLanguage}`,
                pollInterval: 2000,
            },
            onStart: () => {
                dispatch(setIsContentGenerating(true));

                const generatedContentData = {
                    isBotResponding: true,
                }
                updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, generatedContentData);
            },
            onProgress: (message) => {

                // FIX: Don't set isContentGenerating if request is complete
                if (!botRequestRef.current) {
                    logInfo('PROGRESS_AFTER_COMPLETE', 'Ignoring progress update after completion');
                    return;
                }

                dispatch(setIsContentGenerating(true));
                let generatedContentData = {
                    apiProgressData: message,
                    isBotResponding: true,
                }
                updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, generatedContentData);
            },
            onStream: () => {
            },
            onSuccess: (res) => {
                logInfo('BOT_REQUEST_SUCCESS', 'Bot request successful', res);
                dispatch(setContentGenerationData(res));
                dispatch(setIsContentGenerating(false));
                enableButtonAndInputAfterResponse("onSuccess");
            },
            onError: (err) => {
                console.log('Content generation error:', err);

                dispatch(setIsContentGenerating(false));
                dispatch(setContentGenerationError(err));
                contentGenerationError();
                botRequestRef.current = false;
                isProcessingResponseRef.current = false;
            },
            onCancel: (cancelInfo) => {
                logInfo('BOT_REQUEST_CANCELLED', 'Bot request was cancelled', cancelInfo);
                {
                    logInfo('LANG_DETECT_COMPLETE', 'Language detection request completed');
                }
                dispatch(setIsContentGenerating(false));
                botRequestRef.current = false;
                isProcessingResponseRef.current = false;
            },
            onComplete: () => {
                dispatch(clearInputValue());
                dispatch(setIsContentGenerating(false));
                enableButtonAndInputAfterResponse("onComplete");
                botRequestRef.current = false;
                isProcessingResponseRef.current = false;
            },
        });
    }

    /**
     * Resets the state of the message input bar and voice flow after a message has been sent or a response has been received.
     * This includes clearing the input value, resetting the hasSent flag, resetting the current request object, and resetting the voice flow transcript states.
     */
    const cleanupAfterResponse = () => {
        dispatch(clearInputValue());
        hasSentRef.current = false;
        isProcessingResponseRef.current = false;
        currentRequestRef.current = {
            userMessageId: null,
            botMessageId: null,
            chatId: null,
            inputText: ""
        };

        if (isVoiceFlow || isVoiceFlowLocked) {
            dispatch(setDisplayTranscript(""));
            dispatch(setAccumulatedFinalTranscript(""));
            dispatch(setCurrentInterimTranscript(""));
            dispatch(setTranscriptType(""));
            dispatch(setRecordedBase64Data(null));
            dispatch(updateFlowStates());
        }

        // socket states cleanup
        // setAccumulatedFinalTranscript("");
        // setCurrentInterimTranscript("");
        // setDisplayTranscript("");
        // setTranscriptType("");

    };

    /**
     * Opens a confirmation dialog to start a new grievance.
     * If the user confirms, the current chat is reset and the input value is cleared.
     */
    const handleNewGrievance = () => {
        Swal.fire({
            title: "Start New Grievance",
            text: "Are you sure you want to start a new grievance? This will reset the current chat.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, start new",
            cancelButtonText: "No, continue",
        }).then((result) => {
            if (result.isConfirmed) {
                if (selectedChatId) {
                    unlockLanguageForChat(selectedChatId);
                }
                dispatch(setSelectedChatId(null));
                dispatch(clearInputValue());
                cleanupAfterResponse();
            }
        });
    };

    /**
     * Submits a grievance from the user.
     * This function sends a POST request to the API with the grievance details.
     * If the request is successful, it will show a success message to the user and reset the current chat and input value.
     * If the request fails, it will show an error message to the user with a suggestion to retry later.
     * @param {object} data The grievance details to be submitted.
     * @returns {Promise} A promise that resolves if the request is successful, or rejects if the request fails.
     */
    const handleSubmitGrievance = async (data, additionalData) => {
        try {
            setIsComplaintRegistering(true);
            logInfo("SUBMIT_GRIEVANCE", "User submitted a grievance", data);



            const grivancePayload = {
                ...additionalData
            }
            console.log("grivancePayload", { grivancePayload });

            logInfo(`SUBMIT_GRIEVANCE_PAYLOAD`, 'Grievance submission payload', grivancePayload);


            registerGrievanceRequest({
                method: "POST",
                url: API_ENDPOINTS.registerGrievance,
                headers: {
                    "Content-Type": "application/json",
                },
                body: grivancePayload,
                abortable: true,
                isEncrypted: true,
                onStart: () => {
                    logInfo(`SUBMIT_GRIEVANCE_INITIATED`, 'Grievance submission initiated', grivancePayload);
                    setIsComplaintRegistering(true);
                    dispatch(setIsGrievanceRegistering(true));
                },
                onProgress: (message) => {
                    logInfo(`SUBMIT_GRIEVANCE_PROGRESS`, 'Grievance submission in progress', message);
                },
                onStream: () => {
                    logInfo(`SUBMIT_GRIEVANCE_STREAM`, 'Grievance submission stream', grivancePayload);
                },
                onSuccess: (res) => {
                    setIsComplaintRegistering(false);
                    dispatch(setIsGrievanceRegistering(false));
                    logInfo(`SUBMIT_GRIEVANCE_SUCCESS`, 'Grievance submission successful', res);

                    if (res && res.status_code !== 200) {
                        Swal.fire({
                            icon: "error",
                            title: t("grievance_submit.failTitle"),
                            html: `
                              ${res && res.response_text
                                    ? `<p>${typeof res.response_text === "string"
                                        ? res.response_text
                                        : res.response_text?.message
                                    } 
                                    </br> 
                                    ${typeof res.response_text === "string" ? "" : res.response_text?.error}</p>`
                                    : `<p>${t("grievance_submit.failMessage")}</p>`
                                }
                            `,
                            confirmButtonText: t("grievance_submit.ok"),
                            confirmButtonColor: "#f44336",
                            backdrop: `rgba(0,0,0,0.6)`,
                        });
                    } else {
                        Swal.fire({
                            icon: "success",
                            title: t("grievance_submit.successTitle"),
                            html: `
                                <div style="
                                    background: linear-gradient(135deg, #f0fff4, #d1fae5);
                                    border-radius: 16px;
                                    padding: 20px;
                                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                                    margin-top: 10px;
                                    animation: fadeIn 0.5s ease-in-out;
                                ">
                                <h3 style="
                                    color: #16a34a;
                                    font-size: 1.2rem;
                                    font-weight: 700;
                                    margin-bottom: 10px;
                                ">
                                    🎉 ${t("grievance_submit.successMessage")}
                                </h3>
                                ${res && res.response_text
                                    ? `
                                        <div style="
                                            margin-top: 15px;
                                            padding: 12px 16px;
                                            background: linear-gradient(90deg, #dcfce7, #bbf7d0);
                                            border: 1px solid #22c55e;
                                            border-radius: 12px;
                                            display: inline-block;
                                            text-align: left;
                                        ">
                                        <p style="color: #065f46; font-weight: 600; margin: 0;">
                                            ${res.response_text}
                                        </p>
                                        </div>
                                    `
                                    : ""
                                }
                                </div>

                                <style>
                                @keyframes fadeIn {
                                    from { opacity: 0; transform: translateY(10px); }
                                    to { opacity: 1; transform: translateY(0); }
                                }
                                </style>
                            `,
                            confirmButtonText: t("grievance_submit.ok"),
                            confirmButtonColor: "#4CAF50",
                            backdrop: `rgba(0,0,0,0.6)`,
                        }).then((result) => {
                            if (result.isConfirmed) {
                                dispatch(setSelectedChatId(null));
                                currentRequestRef.current.chatId = null;
                                dispatch(clearInputValue());
                                setIsNewChat(true);
                                dispatch(setSelectedSocketLanguageData(null));
                                dispatch(setTextLanguageDetectionData(null));
                                setInitialLanguage();
                                cleanupAfterResponse();
                            }
                        });
                    }
                },
                onError: (err) => {
                    console.log('Grievance submission error:', err);
                    setIsComplaintRegistering(false);
                    dispatch(setIsGrievanceRegistering(false));
                    logError(`SUBMIT_GRIEVANCE_ERROR`, 'Grievance submission failed', err);
                    if (err.code === 'ERR_NETWORK' || err.message.includes('Network ')) {
                        Swal.fire({
                            icon: "error",
                            title: t("grievance_submit.failTitle"),
                            html: `<p>${t("grievance_submit.failMessage")}</p>`,
                            confirmButtonText: t("grievance_submit.ok"),
                            confirmButtonColor: "#f44336",
                            backdrop: `rgba(0,0,0,0.6)`,
                        })
                    }
                },
                onCancel: (cancelInfo) => {
                    setIsComplaintRegistering(false);
                    dispatch(setIsGrievanceRegistering(false));
                    logInfo(`SUBMIT_GRIEVANCE_CANCELLED`, 'Grievance submission cancelled', cancelInfo);
                },
                onComplete: () => {
                    setIsComplaintRegistering(false);
                    dispatch(setIsGrievanceRegistering(false));
                    logInfo(`SUBMIT_GRIEVANCE_COMPLETED`, 'Grievance submission completed');
                },
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("grievance_submit.failTitle"),
                html: `
                    <p>${t("grievance_submit.failMessage")}</p>
                    <p>${t("grievance_submit.failSuggestion")}</p>
                `,
                confirmButtonText: t("grievance_submit.ok"),
                confirmButtonColor: "#d33",
                backdrop: `rgba(0,0,0,0.6)`,
            });
        } finally {
            setIsComplaintRegistering(false);
        }
    };

    /**
     * Handles search input change event.
     * @param {object} e - Event object.
     */
    const handleSearchChange = (e) => {
        logInfo('SEARCH_INPUT_CHANGED', 'Search input changed', e.target.value);
        dispatch(setInputValue(e.target.value));
        dispatch(updateFlowStates());
    };

    /**
     * Handles key down event.
     * If the key pressed is "Enter", it will trigger the handleSend function.
     * @param {object} e - Event object.
     */
    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSend();
    };

    /**
     * Copies the given text to the clipboard.
     * @param {string|object} text - Text to be copied.
     * @throws {Error} - If copying to the clipboard fails.
     */
    const handleCopy = async (text) => {
        text = typeof text === 'string' ? text : JSON.stringify(text);
        try {
            await navigator.clipboard.writeText(text);
            logInfo('COPY_TEXT', 'User copied text', { text });
            showSnackbar(t('messageCopied'), "info");
        } catch (err) {
            console.error("Failed to copy text:", err);
            showSnackbar(t('copyFailed'), "error");
        }
    };

    const handleEdit = (query) => {
        dispatch(setInputValue(query));
        dispatch(updateFlowStates());
    };

    const handleLikeResponse = () => showSnackbar(t('responseLiked'), 'success');

    const handleDisLikeResponse = () => showSnackbar(t('responseDisliked'), 'error');

    const handlePlayAudio = () => showSnackbar('Playing audio!', 'info');


    const lockLanguageForChat = (chatId, languageData) => {
        if (chatId && languageData) {
            dispatch(lockTextFlowForChat(chatId));
            logInfo('LANGUAGE_LOCKED', 'Language locked for chat', {
                chatId,
                language: languageData
            });
        }
    };

    const unlockLanguageForChat = (chatId) => {
        if (chatId) {
            dispatch(unlockTextFlowForChat(chatId));
            logInfo('LANGUAGE_UNLOCKED', 'Language unlocked for chat', { chatId });
        }
    };

    // Pass unlock function to BotChatHead
    const handleUnlockLanguage = () => {
        if (selectedChatId) {
            unlockLanguageForChat(selectedChatId);
            showSnackbar(t('languageUnlocked'), "info");
        }
    };


    /**
     * Handles user's request to regenerate a response from the bot.
     * This function will generate a new user message and a new bot message with the same query as the user message,
     * and then trigger the language detection request.
     * @param {object} botChat - Bot chat object.
     * @param {object} userChat - User chat object.
     */
    const handleRegenerate = async (botChat, userChat) => {
        logInfo('REGENERATE_RESPONSE', 'User requested to regenerate response', { botChat, userChat });

        const userMessageId = generatedUserMessageId();
        const botMessageId = generatedBotMessageId();
        const chatId = botChat.chatId;

        dispatch(setSelectedChatId(chatId));
        dispatch(setCurrentBotChatId(botMessageId));
        currentRequestRef.current = {
            // isVoiceFlow: false,
            userMessageId: userMessageId,
            botMessageId: botMessageId,
            chatId: chatId,
            inputText: userChat.query
        };

        const userMessage = createMessage({
            content: userChat.query,
            userData: { name: userChat.senderName },
            chatId: chatId,
            overrides: {
                id: userMessageId,
                timestamp: generateTimestamp(),
                status: MESSAGE_STATUSES.SENT,
                direction: MESSAGE_DIRECTIONS.OUTGOING,
                isLanguageDetecting: (isVoiceFlow || isVoiceFlowLocked) ? false : true,
                contentAudio: "",
                languageDetectedMessage: t('languageDetecting'),
            },
        });

        const botMessage = createMessage({
            content: "",
            userData: { name: botChat.senderName },
            chatId: chatId,
            overrides: {
                id: botMessageId,
                timestamp: generateTimestamp(),
                status: MESSAGE_STATUSES.PENDING,
                direction: MESSAGE_DIRECTIONS.INCOMING,
                contentAudio: "",
                languageDetectedMessage: null,
                isBotResponding: true,
                isAudioLoading: true,
                isLanguageDetecting: (isVoiceFlow || isVoiceFlowLocked) ? false : true,
                isErrorMessage: false,
                errorMessage: null,
                isNotRelatedToCenter: false,
                isNotRelatedToGrievance: false,
                serverRemarks: ''
            },
        });

        dispatch(setInputValue(userChat.query));
        dispatch(updateFlowStates());
        addMessage(userMessage);
        addMessage(botMessage);

        setIsChatStarted(true);

        try {
            await languageDetectionRequest({
                method: "POST",
                url: API_ENDPOINTS.languageDetection,
                headers: { "Content-Type": "application/json" },
                body: { query: userChat.query },
                abortable: true,
                isEncrypted: true,
                onStart: () => {
                    logError('LANG_DETECT_START', 'Starting language detection request');
                    dispatch(setIsTextLanguageDetecting(true));
                },
                onSuccess: (res) => {
                    logInfo('LANG_DETECT_SUCCESS', 'Language detection successful', res);
                    dispatch(setTextLanguageDetectionData(res));
                },
                onError: (err) => {
                    logError('LANG_DETECT_ERROR', 'Language detection failed', err);
                    dispatch(setTextLanguageDetectionError(err));
                    updateMessage(chatId, botMessageId, {
                        content: t('cannotUnderstand'),
                        status: "received",
                        isBotResponding: false,
                    });
                },
                onComplete: () => {
                    logInfo('LANG_DETECT_COMPLETE', 'Language detection request completed');
                    dispatch(setIsTextLanguageDetecting(false));
                },
            });

        } catch (error) {
            console.error("Error in handleRegenerate:", error);
            updateMessage(chatId, botMessageId, {
                content: t('cannotUnderstand'),
                status: "received",
                isBotResponding: false,
            });
        }
    }

    /**
     * Scrolls the last bot message in the selected chat to the bottom of the viewport.
     * This function is used to update the scroll position after a new message is added to the chat.
     */
    const scrollToLastBotMessage = () => {
        const className = `${selectedChatId}_${currentBotChatId}`;
        const elements = document.getElementsByClassName(className);
        const messageElement = elements.length ? elements[0] : null;
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        logInfo(`CHECKING_SCROLL_MESSAGE`, `Chaecking scroll status`, {

        })
    };

    /**
     * Handles user input and sends a message to the bot.
     * If the input is valid (not empty and not just whitespace), it will trigger the language detection request.
     * If the language detection is successful, it will generate a content generation request.
     * If the content generation is successful, it will update the chat messages with the generated content.
     * If the language detection or content generation fails, it will update the chat messages with an error message.
     * @param {string} inputValue - User input text
     */
    const handleSend = async () => {
        dispatch(updateFlowStates());
        logInfo('HANDLE_SEND', 'User triggered handleSend', {
            isVoiceFlow,
            // onRecordedAudio,
            currentInterimTranscript,
            inputValue
        });

        // ✅ Track if we need to clear audio
        let shouldClearAudio = false;
        let effectiveAudioData = onRecordedAudio;

        // Clear audio if text is typed but transcripts are empty
        if (inputValue.trim() &&
            !currentInterimTranscript.trim() &&
            !accumulatedFinalTranscript.trim() &&
            onRecordedAudio) {
            logInfo('CLEARING_STALE_AUDIO', 'Clearing audio data as text input is present without transcripts');
            dispatch(setRecordedBase64Data(null));
            shouldClearAudio = true;
            effectiveAudioData = null; // Use null for validation
        }

        // Clear audio if no input at all but audio exists
        if (!inputValue.trim() &&
            !currentInterimTranscript.trim() &&
            !accumulatedFinalTranscript.trim() &&
            onRecordedAudio) {
            logInfo('CLEARING_STALE_AUDIO', 'Clearing audio data as no text input present');
            dispatch(setRecordedBase64Data(null));
            shouldClearAudio = true;
            effectiveAudioData = null; // Use null for validation
        }

        console.log('After clearing check:', {
            onRecordedAudio: effectiveAudioData || 'N/A',
            inputValue: inputValue || 'N/A',
            currentInterimTranscript: currentInterimTranscript || 'N/A',
            accumulatedFinalTranscript: accumulatedFinalTranscript || 'N/A',
            shouldClearAudio
        });


        const isInputValid = ((!effectiveAudioData && inputValue.trim()) || (effectiveAudioData && (currentInterimTranscript.trim() !== "" || accumulatedFinalTranscript.trim() !== "")));

        if (!isInputValid) {
            logInfo('HANDLE_SEND_INVALID', 'Invalid input, not sending', {
                hasAudio: !!effectiveAudioData,
                inputValue: inputValue?.substring(0, 20),
                transcript: currentInterimTranscript?.substring(0, 20)
            });
            return;
        }

        setIsChatUpdated(false);

        const chatId = selectedChatId !== null ? selectedChatId : generateChatId();
        currentRequestRef.current.chatId = chatId;
        dispatch(setSelectedChatId(chatId));
        setFilledForms(null);
        logInfo('CHAT_ID', 'Using chat ID', { chatId, selectedChatId });

        const userMessageId = generatedUserMessageId();
        const botMessageId = generatedBotMessageId();
        dispatch(setCurrentBotChatId(botMessageId));
        dispatch(setCurrentUserChatId(userMessageId));
        dispatch(setInputDisabled(true));
        dispatch(setSendButtonDisabled(true));
        dispatch(setMicButtonDisabled(true));
        const inputText = isVoiceFlow ? (accumulatedFinalTranscript || currentInterimTranscript) : inputValue;

        currentRequestRef.current = {
            shouldAutoPlay: isVoiceFlow,
            userMessageId: userMessageId,
            botMessageId: botMessageId,
            chatId: chatId,
            inputText: inputText
        };

        logInfo('MESSAGE_CREATION', 'Creating user and bot messages', {
            isVoiceFlow,
            hasAudio: !!effectiveAudioData,
            transcript: accumulatedFinalTranscript?.substring(0, 30),
            inputValue: inputText
        });
        const userMessage = createMessage({
            content: inputText,
            userData: { name: userData.name },
            chatId,
            overrides: {
                id: userMessageId,
                timestamp: generateTimestamp(),
                status: MESSAGE_STATUSES.SENT,
                direction: MESSAGE_DIRECTIONS.OUTGOING,
                isLanguageDetecting: (isVoiceFlow || isVoiceFlowLocked) ? false : true,
                contentAudio: isVoiceFlow ? effectiveAudioData : "",
                languageDetectedMessage: t('languageDetecting'),
                messageType: 'text',
                query: inputText,
                isVoiceFlow: isVoiceFlow,
                isVoiceFlowLocked: isVoiceFlowLocked ? true : false,
            },
        });

        const botMessage = createMessage({
            content: "",
            userData: { name: "Bot" },
            chatId,
            overrides: {
                id: botMessageId,
                timestamp: generateTimestamp(),
                status: MESSAGE_STATUSES.PENDING,
                direction: MESSAGE_DIRECTIONS.INCOMING,
                contentAudio: "",
                languageDetectedMessage: null,
                isBotResponding: true,
                isAudioLoading: false,
                isLanguageDetecting: (isVoiceFlow || isVoiceFlowLocked) ? false : true,
                query: inputText,
                isVoiceFlow: (isVoiceFlow || isVoiceFlowLocked) ? true : false,
                isTextFlow: (isTextFlow || isTextFlowLocked) ? true : false,
                isVoiceFlowLocked: isVoiceFlowLocked ? true : false,
                shouldAutoPlay: isVoiceFlow
            },
        });

        addMessage(userMessage);
        addMessage(botMessage);
        setIsChatStarted(true);

        try {
            if (!(isVoiceFlow || isVoiceFlowLocked)) {
                if (isTextFlowLocked && selectedTextLanguageData) {
                    // Use the locked language instead of detecting
                    logInfo('USING_LOCKED_LANGUAGE', 'Using locked language for chat', {
                        chatId,
                        language: selectedTextLanguageData
                    });

                    const lockedLang = selectedTextLanguageData.lang;
                    const lockedLangMessage = t('langDetectionMessage', {
                        lang: lockedLang,
                        defaultValue: t('langDetectionMessage', { lang: lockedLang }),
                    });

                    const lockedLanguageUpdate = {
                        isLanguageDetecting: false,
                        contentLanguage: lockedLang,
                        languageDetectedMessage: lockedLangMessage,
                    };

                    updateMessage(chatId, userMessageId, lockedLanguageUpdate);
                    updateMessage(chatId, botMessageId, lockedLanguageUpdate);

                    // Proceed directly to content generation with locked language
                    handleContentGeneration(chatId);
                } else if (isAsciiLettersOrNumbers.test(inputText)) {
                    dispatch(setTextLanguageDetectionData({ input_query: inputText, lang: 'en' }));
                } else {
                    await languageDetectionRequest({
                        method: "POST",
                        url: API_ENDPOINTS.languageDetection,
                        headers: { "Content-Type": "application/json" },
                        body: { query: inputText },
                        abortable: true,
                        isEncrypted: true,
                        onStart: () => {
                            logInfo('LANG_DETECT_START', 'Starting language detection request');
                            dispatch(setIsTextLanguageDetecting(true));
                        },
                        onSuccess: (res) => {
                            logInfo('LANG_DETECT_SUCCESS', 'Language detection successful', res);
                            if (!res || !res.lang || res.lang === 'unknown') {
                                dialog.showDialog({
                                    type: 'error',
                                    title: 'Language Detection Failed',
                                    message: 'Unable to detect language from the text. Please try again.',
                                    confirmText: 'Close',
                                });
                            }
                            lockLanguageForChat(chatId, res);
                            dispatch(setTextLanguageDetectionData(res));
                        },
                        onError: (err) => {
                            logError('LANG_DETECT_ERROR', 'Language detection failed', err);
                            dispatch(setTextLanguageDetectionError(err));
                        },
                        onComplete: () => {
                            logInfo('LANG_DETECT_COMPLETE', 'Language detection request completed');
                            dispatch(setIsTextLanguageDetecting(false));
                        },
                    });
                }
            } else {
                // For voice flow, language was already detected during recording
                // Use the detected language from socket state
                logInfo('VOICE_FLOW_LANG', 'Using language from voice detection', {
                    language: selectedSocketLanguageData?.code?.trim() || 'en'
                });



                let lang = (isVoiceFlow || isVoiceFlowLocked)
                    ? (selectedSocketLanguageData?.code?.trim() || "en")
                    : (selectedTextLanguageData?.lang || "en");

                const defaultLangMsg = t('langDetectionMessage', {
                    lang: lang,
                    defaultValue: t('langDetectionMessage', { lang: lang }),
                });

                // console.log('Detected language:', {
                //     apiLang: lang,
                //     currenLang: getLanguage(),
                //     defaultLangMsg
                // });

                setSelectedLanguageData({ code: lang, label: getLanguageData(lang)?.label });
                setSelectedLanguage(getLanguageData(lang)?.label);

                // ✅  FIX: Handle locked language case
                let languageDetectedMessageToUse = defaultLangMsg;

                if (isTextFlowLocked && selectedTextLanguageData?.lang) {
                    const lockedLang = selectedTextLanguageData.lang;
                    languageDetectedMessageToUse = t('langDetectionMessage', {
                        lang: lockedLang,
                        defaultValue: t('langDetectionMessage', { lang: lockedLang }),
                    });
                    logInfo('USING_LOCKED_LANGUAGE_MESSAGE', 'Using locked language message', {
                        lockedLang,
                        message: languageDetectedMessageToUse
                    });
                }

                const dataToBeUpdate = {
                    isLanguageDetecting: false,
                    contentLanguage: lang,
                    languageDetectedMessage: languageDetectedMessageToUse,
                };

                updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.userMessageId, dataToBeUpdate);
                updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, dataToBeUpdate);

                handleContentGeneration(chatId);
            }
        } catch (error) {
            console.error("Error in handleSend:", error);
            updateMessage(chatId, botMessageId, {
                content: t('cannotUnderstand'),
                status: MESSAGE_DIRECTIONS.INCOMING,
                isBotResponding: false,
            });
        }

        logInfo('HANDLE_SEND_IN_LAST', 'User triggered handleSend', {
            isVoiceFlow,
            // effectiveAudioData,
            currentInterimTranscript,
            inputValue
        });

    };

    /**
     * Called when a suggested text is clicked.
     * It will update the input text by appending the suggested text
     * and then hide the transliterate suggestions.
     * @param {string} data - The suggested text to append to the input text.
     */
    const onSuggestedItemClick = (data) => {
        dispatch(
            setInputValue((prev) => {
                const lastSpaceIndex = prev.trim().lastIndexOf(" ");
                const newText = prev.slice(0, lastSpaceIndex) + " " + data;
                return newText.trim();
            })
        );
        dispatch(updateFlowStates());

        setShowTranslitrateSuggestions(false);
    };


    useEffect(() => {
        return () => {
            dispatch(clearInputValue());
            clearData();
        };
    }, []);

    useEffect(() => {
        if (isNewChat) {
            clearData();
        }
    }, [isNewChat]);

    useEffect(() => {
        if (isNewChatClicked) {
            // Clear all refs
            hasSentRef.current = false;
            botRequestRef.current = false;
            isTypingRef.current = false;
            prevMessageCountRef.current = 0;

            currentRequestRef.current = {
                userMessageId: null,
                botMessageId: null,
                chatId: null,
                inputText: ""
            };

            // Clear all state
            clearData();

            // Reset the flag
            dispatch(setIsNewChatClicked(false));

            logInfo('NEW_CHAT_CLEANUP', 'Cleaned up state for new chat');
        }
    }, [isNewChatClicked]);

    const sendApiErrorToServer = (message, responseData, errorInfo, inputQuery) => {
        return trackApiError({
            message: message,
            status: 0,
            statusText: "React Backend API Error",
            url: window.location.href,
            method: "API_CALL_ERROR",
            responseData: responseData,
            requestId: null,
            duration: null,
            attempts: 1,
            timeout: null,
            curl: "N/A",
            headers: null,
            requestData: inputQuery,
            component: "ChatPage",
            errorInfo: errorInfo,
            timestamp: new Date().toISOString(),
        })
    };

    useEffect(() => {
        if (botRequestRef.current || isLanguageDetecting) return;

        const debounceTimer = setTimeout(() => {
            const socketTranscriptionExists = !!accumulatedFinalTranscript?.trim() || !!currentInterimTranscript?.trim();
            const audioExists = !!onRecordedAudio;
            const searchTextExists = !!inputValue?.trim();

            const voiceInputValid = socketTranscriptionExists && audioExists;
            const textInputValid = searchTextExists && !socketTranscriptionExists && !audioExists;
            const hasValidMessages = currentRequestRef.current.userMessageId && currentRequestRef.current.botMessageId;

            if (!hasValidMessages) {
                return;
            }

            if (langDetectError) {
                logError('LANG_DETECT_ERROR', 'Language detection failed', langDetectError);
                showSnackbar(t('languageDetectionFailed'), "error");
                const dataToBeUpdate = {
                    isLanguageDetecting: false,
                };
                updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, dataToBeUpdate);
                return;
            }

            logInfo('LANG_DETECT_DATA', 'Language detection data received', selectedTextLanguageData);


            let lang = null;

            if (
                (isVoiceFlow || isVoiceFlowLocked) &&
                selectedSocketLanguageData?.code?.trim()
            ) {
                lang = selectedSocketLanguageData.code.trim();
            } else if (
                !isVoiceFlow &&
                !isVoiceFlowLocked &&
                selectedTextLanguageData?.lang?.trim()
            ) {
                lang = selectedTextLanguageData.lang.trim();
            } else if (selectedLanguageData?.code?.trim()) {
                lang = selectedLanguageData.code.trim();
            }

            if (lang) {
                setLanguage(lang);
            }

            logInfo('LANG_CHECK', 'checking the language', {
                apiLangData: selectedTextLanguageData,
                apiLang: lang,
                currenLang: getLanguage()
            })

            if (!lang) {
                let currenLang = (isVoiceFlow || isVoiceFlowLocked)
                    ? (selectedSocketLanguageData?.code?.trim() || "en")
                    : (selectedTextLanguageData?.lang || "en");
                lang = currenLang;
            }


            // console.log('Detected language:', {
            //     apiLang: lang,
            //     currenLang: getLanguage()
            // });




            const defaultLangMsg = t('langDetectionMessage', {
                lang: lang,
                defaultValue: t('langDetectionMessage', { lang: lang }),
            });


            // console.log('Detected language:', {
            //     apiLang: lang,
            //     currenLang: getLanguage(),
            //     defaultLangMsg
            // });

            setSelectedLanguageData({ code: lang, label: getLanguageData(lang)?.label });
            setSelectedLanguage(getLanguageData(lang)?.label);

            // ✅  FIX: Handle locked language case
            let languageDetectedMessageToUse = defaultLangMsg;

            if (isTextFlowLocked && selectedTextLanguageData?.lang) {
                const lockedLang = selectedTextLanguageData.lang;
                languageDetectedMessageToUse = t('langDetectionMessage', {
                    lang: lockedLang,
                    defaultValue: t('langDetectionMessage', { lang: lockedLang }),
                });
                logInfo('USING_LOCKED_LANGUAGE_MESSAGE', 'Using locked language message', {
                    lockedLang,
                    message: languageDetectedMessageToUse
                });
            }

            const dataToBeUpdate = {
                isLanguageDetecting: false,
                contentLanguage: lang,
                languageDetectedMessage: languageDetectedMessageToUse,
            };

            updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.userMessageId, dataToBeUpdate);
            updateMessage(selectedChatId ? selectedChatId : currentRequestRef.current.chatId, currentRequestRef.current.botMessageId, dataToBeUpdate);

            const shouldSendVoice = voiceInputValid;
            const shouldSendText = textInputValid;


            if (shouldSendText || shouldSendVoice) {
                const isVoice = shouldSendVoice;
                logInfo('BOT_REQUEST_INITIATED', isVoice ? 'Voice input detected, sending transcript to bot' : 'Text input detected, sending message to bot', {
                    currentRequestRef: currentRequestRef.current,
                    isLanguageDetecting,
                    langDetectError,
                    selectedTextLanguageData,
                    currentInterimTranscript,
                    onRecordedAudio,
                    selectedChatId: selectedChatId ? selectedChatId : currentRequestRef.current.chatId
                });

                if (isVoice) botRequestRef.current = true;

                logInfo('FINAL_INPUT', 'Final input being sent to bot', {
                    text: currentRequestRef.current.inputText,
                    lang,
                    isVoiceFlow: isVoiceFlow
                });

                handleContentGeneration(selectedChatId ? selectedChatId : currentRequestRef.current.chatId);  // ✅ Single call
            } else {
                // Invalid input state - don't call the API
                logInfo('INVALID_INPUT_STATE', 'Invalid input state, skipping bot call', {
                    voiceInputValid,
                    textInputValid,
                    accumulatedFinalTranscript,
                    onRecordedAudio,
                    inputValue
                });
                // DON'T call handleContentGeneration here!
            }
        }, 200);

        return () => clearTimeout(debounceTimer);
    }, [
        isLanguageDetecting,
        langDetectError,
        selectedTextLanguageData,
        selectedChatId,
        isVoiceFlow,
        isVoiceFlowLocked,
        isTextFlowLocked,
        selectedSocketLanguageData,
        selectedLanguageData,
        accumulatedFinalTranscript,
        currentInterimTranscript,
        onRecordedAudio,
        inputValue,
        setLanguage,
    ]);

    const contentGenerationError = () => {
        const generatedContentData = {
            isError: true,
            isBotResponding: false,
            isLanguageDetecting: false,
            isAudioLoading: false,
            status: MESSAGE_DIRECTIONS.INCOMING,
            timestamp: generateTimestamp(),
        }
        updateMessage(selectedChatId, currentRequestRef.current.botMessageId, generatedContentData);

        sendApiErrorToServer(
            'Content Generation Error',
            contentGenError,
            { errorMessage: contentGenError?.message },
            currentRequestRef.current.inputText
        );

        cleanupAfterResponse();
        setIsChatUpdated(true);
    }

    useEffect(() => {
        if (!currentRequestRef.current.botMessageId || !selectedChatId) {
            return;
        }

        // CRITICAL: Skip if we're already processing or if botRequest is complete
        if (isProcessingResponseRef.current || !botRequestRef.current) {
            return;
        }

        if (isContentGenerating) {
            const generatedContentData = {
                isBotResponding: true,
            }
            updateMessage(selectedChatId, currentRequestRef.current.botMessageId, generatedContentData);
            setIsChatUpdated(true);
        } else if (contentGenError) {
            isProcessingResponseRef.current = true;
            showSnackbar(t('contentGenerationFailed'), "error");
            contentGenerationError();
        } else if (contentGenData) {
            isProcessingResponseRef.current = true;
            try {
                let botMessage = null;
                let isErrorMessage = contentGenData['isErrorMessage'] || false;
                if (isErrorMessage) {
                    let errorMessage = contentGenData['errorMessage'] || '';
                    botMessage = {
                        isErrorMessage: true,
                        errorMessage: errorMessage,
                        status: MESSAGE_DIRECTIONS.INCOMING,
                        isBotResponding: false,
                        isAudioLoading: false,
                        messageType: 'text',
                        timestamp: generateTimestamp(),
                    }
                    sendApiErrorToServer('Content Generation Error from API', contentGenData, { errorMessage }, currentRequestRef.current.inputText);
                } else {

                    let isNotRelatedToGrievance = contentGenData['isNotRelatedToGrievance'] || false;
                    let isNotSubcategory = contentGenData['isNotSubcategory'] || false;
                    let regionalResponse = contentGenData['Regional_Response'];
                    let standardResponse = contentGenData['response'];
                    let grievanceBucket = {};
                    let serverRemarks = contentGenData['server_remarks'] || '';

                    const stateName = contentGenData['stateName'];
                    const stateCode = contentGenData['stateCode'];
                    const hasValidState = typeof stateName === 'string' && stateName.trim() !== '' && typeof stateCode === 'string' && stateCode.trim() !== '';
                    let isNotRelatedToCenter = hasValidState ? Boolean(contentGenData['isNotRelatedToCenter']) : false;

                    try {
                        // gardrail check
                        if (isNotRelatedToGrievance) {
                            botMessage = {
                                content: regionalResponse,
                                contentStandard: standardResponse,
                                contentAudio: contentGenData['audio'],
                                botResponse: regionalResponse,
                                status: MESSAGE_DIRECTIONS.INCOMING,
                                isBotResponding: false,
                                isAudioLoading: false,
                                messageType: 'text',
                                timestamp: generateTimestamp(),
                                isNotRelatedToGrievance: isNotRelatedToGrievance,
                                isNotSubcategory: isNotSubcategory,
                            }
                            // state check 
                        } else if (isNotRelatedToCenter) {
                            botMessage = {
                                content: regionalResponse,
                                contentStandard: standardResponse,
                                contentAudio: contentGenData['audio'],
                                serverRemarks: serverRemarks,
                                user_query: contentGenData['user_query'] || '',
                                stateName: contentGenData['stateName'] || '',
                                stateCode: contentGenData['stateCode'] || '',
                                stateName_regional: contentGenData['stateName_regional'] || '',
                                botResponse: regionalResponse,
                                status: MESSAGE_DIRECTIONS.INCOMING,
                                isBotResponding: false,
                                isAudioLoading: false,
                                messageType: 'text',
                                timestamp: generateTimestamp(),
                                isNotRelatedToCenter: isNotRelatedToCenter,
                                isNotSubcategory: isNotSubcategory,
                            }
                        } else {
                            if (typeof regionalResponse === 'string') {
                                const trimmed = regionalResponse.trim();
                                if (trimmed.startsWith('{')) {
                                    const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
                                    regionalResponse = parsed;
                                    grievanceBucket = parsed?.grievance_bucket || {};
                                }
                            } else if (typeof regionalResponse === 'object' && regionalResponse !== null) {
                                grievanceBucket = regionalResponse?.grievance_bucket || {};
                            }


                            if (typeof standardResponse === 'string') {
                                const trimmed = standardResponse.trim();
                                if (trimmed.startsWith('{')) {
                                    const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
                                    standardResponse = parsed;
                                    grievanceBucket = parsed?.grievance_bucket || {};
                                }
                            } else if (typeof standardResponse === 'object' && standardResponse !== null) {
                                grievanceBucket = standardResponse?.grievance_bucket || {};
                            }

                            // SUCCESS CASE ERROR LOGGING CONDITIONS
                            const isRegionalEmpty =
                                typeof regionalResponse === "string" &&
                                (regionalResponse.trim() === "" ||
                                    (regionalResponse.trim().toLowerCase() === "only form fields" && contentGenData.field_form?.length === 0));

                            const isStandardEmpty =
                                typeof standardResponse === "string" &&
                                (standardResponse.trim() === "" ||
                                    (standardResponse.trim().toLowerCase() === "only form fields" && contentGenData.field_form?.length === 0));


                            if (isRegionalEmpty || isStandardEmpty) {
                                sendApiErrorToServer('Content Generation Error from API', contentGenData, { errorMessage: 'Empty response from bot' }, currentRequestRef.current.inputText);
                            }

                            botMessage = {
                                content: regionalResponse,
                                contentStandard: standardResponse,
                                contentAudio: contentGenData['audio'],
                                field_form: contentGenData['field_form'] || {},
                                category_code: contentGenData['category_code'] || null,
                                org_code: contentGenData['org_code'] || "",
                                field_form_list: contentGenData['field_form_list'] || [],
                                mandatory_data_present: contentGenData['mandatory_data_present'],
                                mandatory_questions_answered: contentGenData['mandatory_questions_answered'],
                                mandatory_questions_asked: contentGenData['mandatory_questions_asked'],
                                grievance_bucket: grievanceBucket,
                                botResponse: regionalResponse,
                                updated_grievance: contentGenData['updated_grievance'] || "",
                                status: MESSAGE_DIRECTIONS.INCOMING,
                                isBotResponding: false,
                                isAudioLoading: false,
                                messageType: 'text',
                                timestamp: generateTimestamp(),
                                isNotRelatedToGrievance: isNotRelatedToGrievance,
                                serverRemarks: serverRemarks,
                                isNotSubcategory: isNotSubcategory,
                            }
                        }
                    } catch (err) {
                        logError('REGIONAL_RESPONSE_PARSE_ERROR', 'Failed to parse Regional_Response', err);
                        sendApiErrorToServer('Content Generation Error from API', contentGenData, { errorMessage: 'Failed to parse Regional_Response' }, currentRequestRef.current.inputText);
                    }
                }

                updateMessage(selectedChatId, currentRequestRef.current.botMessageId, botMessage);
                cleanupAfterResponse();
                setIsChatUpdated(true);
            } catch (err) {
                logError('CONTENT_GEN_PROCESSING_ERROR', 'Error processing content generation data', err);
                sendApiErrorToServer('Content Generation Processing Error', contentGenData, { errorMessage: err.message }, currentRequestRef.current.inputText);
            }
        } else if (contentGenData === null && currentRequestRef.current.botMessageId) {
            let generatedContentData = {
                content: t('noResponse'),
                status: MESSAGE_DIRECTIONS.INCOMING,
                isBotResponding: false,
                isAudioLoading: true
            }
            updateMessage(selectedChatId, currentRequestRef.current.botMessageId, generatedContentData);
        }
        // enableButtonAndInputAfterResponse("useEffect");
    }, [isContentGenerating, contentGenData, contentGenError]);

    function isFormValid(formData) {
        if (!formData?.field_form?.InputFields) return false;

        const inputFields = formData.field_form.InputFields;

        for (const field of inputFields) {
            if (field.IsHidden) continue;

            if (field.IsMandatory) {
                if (field.FieldType === "Dropdown") {
                    if (!field.UserInputValue || !field.UserInputText) {
                        console.warn(`❌ Missing dropdown value for ${field.FieldName}`);
                        return false;
                    }
                } else {
                    if (
                        field.UserInputValue === null ||
                        field.UserInputValue === undefined ||
                        field.UserInputValue === ""
                    ) {
                        console.warn(`❌ Missing value for ${field.FieldName}`);
                        return false;
                    }
                }
            }
        }
        return true;
    }


    useEffect(() => {
        if (onDynamicFormSubmit && getFormAnswers && Object.keys(getFormAnswers).length > 0) {
            dispatch(setInputValue(Object.values(getFormAnswers).join(',')));
            dispatch(updateFlowStates());
            logInfo('DYNAMIC_FORM_ANSWERS', 'Dynamic form answers', { getFilledForms });
            logInfo('DYNAMIC_FORM_ANSWERS', 'Dynamic form answers set to input value', { getFormAnswers });
            if (isFormValid(getFilledForms)) {
                logInfo('DYNAMIC_FORM_VALIDATION', 'Dynamic form is valid', { getFilledForms });
            } else {
                logInfo('DYNAMIC_FORM_VALIDATION', 'Dynamic form is not valid', { getFilledForms });
            }
        }
    }, [onDynamicFormSubmit]);

    useEffect(() => {
        if (isVoiceFlow || isVoiceFlowLocked) {
            // Combine exactly like the display shows
            const combinedTranscript = accumulatedFinalTranscript +
                (accumulatedFinalTranscript && currentInterimTranscript ? " " : "") +
                currentInterimTranscript;

            if (combinedTranscript.trim()) {
                dispatch(setInputValue(combinedTranscript));
                dispatch(updateFlowStates());
                logInfo('VOICE_TRANSCRIPT_UPDATE', 'Voice transcript updated', {
                    final: accumulatedFinalTranscript?.substring(0, 50),
                    interim: currentInterimTranscript?.substring(0, 50),
                    combined: combinedTranscript.substring(0, 100)
                });
            }
        }
    }, [isVoiceFlow, isVoiceFlowLocked, accumulatedFinalTranscript, currentInterimTranscript]);


    useEffect(() => {
        if (onDynamicFormSubmit && inputValue && inputValue.trim() !== "") {
            handleSend();
            setDynamicFormSubmit(false);
            logInfo('DYNAMIC_FORM_SUBMIT', 'Dynamic form submitted with answers', { getFormAnswers, inputValue });
        }
    }, [inputValue, onDynamicFormSubmit]);

    useEffect(() => {
        if (!selectedChatId) return;

        const selectedChat = chatList.find(c => c.chatId === selectedChatId);
        if (!selectedChat) return;

        const messages = selectedChat.chatMessages || [];
        const messageCount = messages.length;
        const lastMessage = messages.at(-1);

        // Skip scrolling if user is typing
        if (isTypingRef?.current) {
            logInfo("ChatPage.SKIP_SCROLL", "User typing, skipping scroll");
            return;
        }

        // Scroll only if a new message is added
        if (messageCount > prevMessageCountRef.current) {
            const isBotMessage = lastMessage?.sender === "bot";
            const isIncomingMessage = lastMessage?.direction === "incoming";

            if (isBotMessage || isIncomingMessage) {
                logInfo("ChatPage.SCROLLIN_FROM_EFFECT", "Scrolling from effect", { selectedChatId });
                setTimeout(() => scrollToLastBotMessage(), 150);
            }
        }

        // Update previous message count
        prevMessageCountRef.current = messageCount;
    }, [chatList, selectedChatId]);

    useEffect(() => {
        logInfo('PRE_AUTO_SEND_TRIGGER', 'Checking voice journey status', {
            isVoiceFlow,
            isVoiceFlowLocked,
            isAudioJourneyReadyToSend,
            accumulatedTranscript: accumulatedFinalTranscript,
            audioDataExists: !!onRecordedAudio,
            hasAlreadySent: hasSentRef.current
        });

        // ✅ Only trigger when ready to send AND haven't sent yet
        if ((isVoiceFlow || isVoiceFlowLocked) && isAudioJourneyReadyToSend && !hasSentRef.current) {
            logInfo('AUTO_SEND_TRIGGERED', 'Voice journey complete, auto-sending', {
                transcript: accumulatedFinalTranscript || currentInterimTranscript,
                audioLength: onRecordedAudio?.length
            });

            currentRequestRef.current.inputText = accumulatedFinalTranscript || currentInterimTranscript;
            hasSentRef.current = true;
            handleSend();
        }
    }, [isVoiceFlow, isVoiceFlowLocked, isAudioJourneyReadyToSend, accumulatedFinalTranscript, currentInterimTranscript, onRecordedAudio]);

    useEffect(() => {
        dispatch(updateFlowStates());
    }, [inputValue, onRecordedAudio, accumulatedFinalTranscript, currentInterimTranscript, dispatch]);

    return (
        <>
            {isComplaintRegistering && <Loader message="Registering your grievance. Please wait..." />}
            <div className="w-full h-full relative shadow-md bg-white dark:bg-slate-600 flex flex-col overflow-hidden transition-all duration-300 ease-in-out">
                <Snackbar
                    className="mt-[56px]"
                    open={copyBar}
                    autoHideDuration={1000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert className="bg-[#000000]" onClose={handleClose} severity="success">
                        {t("textCopied")}
                    </Alert>
                </Snackbar>

                {selectedChatId ? <CollapsedHeader /> : <ExpendedHeader />}

                {!selectedChatId && (
                    <div className="relative overflow-y-auto">
                        <SuggestedQuestions
                            questions={t('suggestedQuestions.suggestedQuestions', { returnObjects: true })}
                            onSuggestedQuestionClick={(question) => {
                                dispatch(setInputValue(question));
                                dispatch(updateFlowStates());
                            }}
                        />
                    </div>
                )}

                <div className="relative flex-1 overflow-y-auto bg-cover bg-center z-0">
                    <div className="relative z-10 h-full px-4 py-2 flex flex-col space-y-3 bg-[#FFFFFF] dark:bg-[#3C4043] transition-all duration-300 ease-in-out">
                        {((chatList && selectedChatId) || isChatUpdated) &&
                            chatList
                                .find(chat => chat.chatId === selectedChatId)?.chatMessages
                                ?.map((chat, index, array) => {

                                    // logInfo('LOAD_CHAT_MESSAGE', 'Loading chat message', {
                                    //     direction: chat.direction === "outgoing" ? "user" : "bot",
                                    //     message: chat
                                    // })

                                    let isLastItem = index === array.length - 1;
                                    return chat.direction === "outgoing" ? (
                                        <UserChatHead
                                            chatData={chat}
                                            id={`${selectedChatId}_${chat.id}`}
                                            key={`user-${chat.id}`}
                                            message={{ text: chat.query, audio: chat.contentAudio }}
                                            timestamp={chat.timestamp}
                                            onCopy={() => handleCopy(chat.query)}
                                            onEdit={() => handleEdit(chat.query)}
                                            userImage={picLogo}
                                            identificationClassName={`${selectedChatId}_${chat.id}`}
                                        />
                                    ) : (
                                        <BotChatHead
                                            identificationClassName={`${selectedChatId}_${chat.id}`}
                                            id={`${selectedChatId}_${chat.id}`}
                                            key={chat.contentAudio ? `bot-${index}-${chat.contentAudio?.slice(0, 10)}` : `bot-${index}`}
                                            response={{
                                                text: chat.content,
                                                contentStandard: chat.contentStandard,
                                                audioUrl: chat.audioUrl,
                                                imageUrl: chat.imageUrl,
                                                contentAudio: chat.contentAudio,
                                                isLanguageDetecting: chat.isLanguageDetecting,
                                                isBotResponding: chat.isBotResponding,
                                                languageDetectedMessage: chat.languageDetectedMessage,
                                                mandatory_data_present: chat.mandatory_data_present,
                                                mandatory_questions_answered: chat.mandatory_questions_answered,
                                                mandatory_questions_asked: chat.mandatory_questions_asked,
                                                grievance_bucket: chat.grievance_bucket,
                                                isError: chat.isError || false,
                                                botResponse: chat.botResponse || {},
                                                updated_grievance: chat.updated_grievance || "",
                                                field_form: chat.field_form || [],
                                                isVoiceFlow: chat.isVoiceFlow || false,
                                                isTextFlow: chat.isTextFlow || false,
                                                isVoiceFlowLocked: chat.isVoiceFlowLocked || false,
                                                nerContent: chat.nerContent,
                                                isNerResponding: chat.isNerResponding,
                                                apiProgressData: chat.apiProgressData || null,
                                                category_code: chat.category_code || null,
                                                org_code: chat.org_code || "",
                                                field_form_list: chat.field_form_list || [],
                                                errorMessage: chat.errorMessage,
                                                isErrorMessage: chat.isErrorMessage,
                                                isNotRelatedToGrievance: chat.isNotRelatedToGrievance,
                                                isNotRelatedToCenter: chat.isNotRelatedToCenter,
                                                serverRemarks: chat.serverRemarks,
                                                user_query: chat.user_query,
                                                stateName: chat.stateName,
                                                stateName_regional: chat.stateName_regional,
                                                stateCode: chat.stateCode,
                                                isNotSubcategory: chat.isNotSubcategory
                                            }}
                                            isMostRecent={isLastItem && (chat.isVoiceFlow || chat.isVoiceFlowLocked)}
                                            chatData={chat}
                                            timestamp={chat.timestamp}
                                            botImage={botLogo}
                                            onCopy={() => handleCopy(chat.content)}
                                            onLike={handleLikeResponse}
                                            onDislike={handleDisLikeResponse}
                                            onPlayAudio={handlePlayAudio}
                                            onEdit={handleEdit}
                                            onDelete={() => deleteMessage(chat.id)}
                                            onRegenerate={() => {
                                                const currentChatList = chatList.find(c => c.chatId === selectedChatId)?.chatMessages || [];
                                                const currentIndex = currentChatList.findIndex(msg => msg.id === chat.id);
                                                const userMessage = currentChatList
                                                    .slice(0, currentIndex)
                                                    .reverse()
                                                    .find(msg => msg.direction === "outgoing");

                                                handleRegenerate(chat, userMessage);
                                            }}
                                            setHasSubmitted={setHasSubmitted}
                                            hasSubmitted={hasSubmitted}
                                            handleSubmitGrievance={handleSubmitGrievance}
                                            setFormAnswers={setFormAnswers}
                                            setFilledForms={setFilledForms}
                                            onDynamicFormSubmit={onDynamicFormSubmit}
                                            setDynamicFormSubmit={setDynamicFormSubmit}
                                            handleSend={handleSend}
                                            onUnlockLanguage={handleUnlockLanguage}
                                            isLanguageLocked={isTextFlowLocked}
                                        />
                                    );
                                })
                        }
                    </div>
                    <div id="fakeView" ref={messagesEndRef} />
                </div>

                {/* This prevents SocketMicStreamer from unmounting during recording */}

                {((chatList && selectedChatId) || isChatUpdated) &&
                    chatList.find(chat => chat.chatId === selectedChatId)?.chatMessages.length === 0 ? (
                    <SepratedInputChnages
                        isUserInputBar={true}
                        isTypingRef={isTypingRef}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        handleSend={handleSend}
                        onSuggestedItemClick={onSuggestedItemClick}
                        placeholder={t('inputPlaceholder')}
                        sendClicked={getFormAnswers !== null && getFormAnswers.length > 0}
                        showTranslitrateSuggestions={showTranslitrateSuggestions}
                    />
                ) : ((chatList && selectedChatId) || isChatUpdated) &&
                    chatList.find(chat => chat.chatId === selectedChatId)?.chatMessages[
                        chatList.find(chat => chat.chatId === selectedChatId)?.chatMessages.length - 1
                    ]?.mandatory_data_present === true ? (
                    hasSubmitted ? (
                        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-xl p-6 my-4 mx-2 shadow-md text-center">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                <AlertCircle className="w-10 h-10 text-yellow-500" />
                                <p className="text-lg font-semibold">
                                    {t("grievance.alreadyRegisteredTitle")}
                                </p>
                                <p className="text-sm text-gray-700 max-w-md">
                                    {t("grievance.alreadyRegisteredMessage")}
                                </p>
                                <button
                                    className="mt-3 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-md transition duration-150"
                                    onClick={handleNewGrievance}
                                >
                                    {t("grievance.startNewGrievanceButton")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-[#F5F7FF] border border-[rgba(18,51,168,0.3)] text-blue-800 rounded-md p-2 mx-2 shadow-md text-center">
                            <div className="flex flex-col items-center justify-center space-y-1">
                                <AlertCircle className="w-7 h-7 text-[#1233A8]" />
                                <p className="text-sm text-gray-700 max-w-xxl">
                                    <Trans i18nKey="grievanceSuccess" components={{ 1: <span className="font-semibold" /> }} />
                                </p>
                            </div>
                        </div>
                    )
                ) : (
                    <SepratedInputChnages
                        isUserInputBar={true}
                        isTypingRef={isTypingRef}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        handleSend={handleSend}
                        onSuggestedItemClick={onSuggestedItemClick}
                        placeholder={t('inputPlaceholder')}
                        sendClicked={getFormAnswers !== null && getFormAnswers.length > 0}
                        showTranslitrateSuggestions={showTranslitrateSuggestions}
                    />
                )}
            </div>
        </>
    );
}

export default ChatPage;